import type React from 'react'
// Тот же признак, что считает НДС на сервере: локальная копия не знала, что
// апартаменты юридически нежилые, и подписывала их «Жилая аренда», пока счёт
// выставлялся как за коммерцию.
import { isResidential } from '@rentflowdev/common/vat'
import { useTranslation } from 'react-i18next'
import { CalendarIcon, HomeIcon, ShieldCheckIcon, UserIcon, WalletIcon } from 'lucide-react'
import { cn } from '@rentflowdev/ui'
import { useGetPropertyQuery } from '@/api'
import type { Rental } from '@/types'
import { CURRENCY_SIGNS, DEFAULT_LOCALE } from '@/types'

// Unified accent-tile header for the rental context (tester feedback #286:
// "make the key info accent tiles and reuse the automation-dialog look").
// Driven entirely by real rental/property data — no hardcoded labels.

const TILE_STYLES = {
	violet: 'bg-violet-50 text-violet-600',
	green: 'bg-emerald-50 text-emerald-600',
	orange: 'bg-orange-50 text-orange-600',
	cyan: 'bg-cyan-50 text-cyan-600',
	neutral: 'bg-(--color-muted) text-(--color-muted-foreground)',
} as const

function formatDateShort(iso?: string): string {
	if (!iso) return '—'
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, {
		day: '2-digit', month: '2-digit', year: '2-digit',
	})
}

function formatMoney(amount?: string, currency?: string): string {
	if (!amount) return '—'
	const num = Number(amount)
	if (!Number.isFinite(num)) return '—'
	const sign = CURRENCY_SIGNS[currency ?? 'RUB'] ?? currency ?? ''
	return `${num.toLocaleString(DEFAULT_LOCALE)} ${sign}`
}

function monthsLeft(endDate?: string): number | null {
	if (!endDate) return null
	const ms = new Date(endDate).getTime() - Date.now()
	if (ms <= 0) return 0
	return Math.ceil(ms / (1000 * 60 * 60 * 24 * 30))
}

function SummaryTile({
	icon, tone, label, value, sub,
}: {
	icon: React.ReactNode
	tone: keyof typeof TILE_STYLES
	label: string
	value: string
	sub?: string
}) {
	return (
		<div className="flex min-w-0 flex-1 items-center gap-3 rounded-(--radius-md) border border-(--color-border) bg-(--color-background) px-4 py-3">
			<div className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', TILE_STYLES[tone])}>
				{icon}
			</div>
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="truncate text-xs text-(--color-muted-foreground)">{label}</span>
				<span className="truncate text-sm font-[500] text-(--color-foreground)">{value}</span>
				{sub && <span className="truncate text-xs text-(--color-muted-foreground)">{sub}</span>}
			</div>
		</div>
	)
}

interface RentalSummaryBarProps {
	rental: Rental
	className?: string
}

export function RentalSummaryBar({ rental, className }: RentalSummaryBarProps) {
	const { t } = useTranslation()
	const { data: property } = useGetPropertyQuery(rental.propertyId, {
		skip: !rental.propertyId,
	})

	const rentTypeLabel = property
		? isResidential({ propertyType: property.type, housingType: property.housingType ?? undefined })
			? t('rent.summaryBar.residential')
			: property.type === 'LAND'
				? t('rent.summaryBar.land')
				: t('rent.summaryBar.commercial')
		: t('rent.summaryBar.rentType')

	const left = monthsLeft(rental.endDate)
	const periodSub =
		left !== null ? t('rent.summaryBar.monthsLeft', { count: left }) : undefined

	return (
		<div className={cn('flex flex-wrap gap-3', className)}>
			<SummaryTile
				icon={<HomeIcon className="size-4.5" />}
				tone="violet"
				label={rentTypeLabel}
				value={rental.propertyAddress ?? rental.propertyTitle ?? '—'}
			/>
			<SummaryTile
				icon={<UserIcon className="size-4.5" />}
				tone="green"
				label={t('rent.summaryBar.tenant')}
				value={rental.tenantName ?? '—'}
			/>
			<SummaryTile
				icon={<CalendarIcon className="size-4.5" />}
				tone="orange"
				label={t('rent.summaryBar.period')}
				value={`${formatDateShort(rental.startDate)} – ${formatDateShort(rental.endDate)}`}
				sub={periodSub}
			/>
			<SummaryTile
				icon={<WalletIcon className="size-4.5" />}
				tone="cyan"
				label={t(`rent.summaryBar.periodicity.${rental.period}`)}
				value={formatMoney(rental.rentAmount, rental.currency)}
			/>
			<SummaryTile
				icon={<ShieldCheckIcon className="size-4.5" />}
				tone="neutral"
				label={t('rent.summaryBar.deposit')}
				value={
					rental.depositAmount
						? formatMoney(rental.depositAmount, rental.depositCurrency ?? rental.currency)
						: t('rent.summaryBar.noDeposit')
				}
			/>
		</div>
	)
}
